import React, { useRef, useState } from 'react';
import { errorMessage, setProjectStatus } from '../../api';
import type { Project } from '../../types';
import { Button, Modal, useToast } from '../../ui';

interface CampaignStatusControlProps {
  project: Project;
  /** the server says the campaign can be launched (GET /project/{id}/readiness: `ready`) */
  launchReady: boolean;
  /** why Launch is disabled, shown as the button's tooltip */
  launchHint?: string;
  /** called after the status changed, so the page can reload */
  onChanged: () => void;
}

/**
 * One action for the campaign's next status: Draft -> Launch, Active -> Close, Closed -> Reopen.
 * The server stores the status as active / draft / closed (PATCH /project/{id}/status).
 * Launching (and reopening) is enabled only when the server's readiness check says `ready`; if the server still
 * refuses, its `errors` are shown, so the checklist and the button can never disagree silently.
 */
export function CampaignStatusControl({ project, launchReady, launchHint, onChanged }: CampaignStatusControlProps) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // synchronous guard against a fast double click
  const [confirmClose, setConfirmClose] = useState(false);
  const status = String(project.projectStatus || '').toLowerCase();

  const change = async (next: 'active' | 'draft' | 'closed', doneMessage: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await setProjectStatus(project.id, next);
      notify(doneMessage, { tone: 'ok' });
      setConfirmClose(false);
      onChanged();
    } catch (err) {
      notify(errorMessage(err), { tone: 'danger', duration: 9000 }); // "Campaign is not ready to launch: <missing items>"
      onChanged(); // the checklist may be out of date: refresh it
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (status === 'draft') {
    return (
      <Button
        variant="secondary"
        loading={busy}
        disabled={!launchReady}
        title={launchReady ? undefined : launchHint}
        onClick={() => change('active', 'Campaign launched')}
      >
        Launch campaign
      </Button>
    );
  }
  if (status === 'active') {
    return (
      <>
        <Button variant="secondary" onClick={() => setConfirmClose(true)}>Close campaign</Button>
        <Modal
          open={confirmClose}
          size="sm"
          title="Close this campaign?"
          subtitle={project.name}
          onClose={() => { if (!busyRef.current) setConfirmClose(false); }}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setConfirmClose(false)} disabled={busy}>Keep it active</Button>
              <Button loading={busy} onClick={() => change('closed', 'Campaign closed')}>Close campaign</Button>
            </>
          )}
        >
          <p className="text-sm text-muted">It moves to the Closed tab. You can reopen it later.</p>
        </Modal>
      </>
    );
  }
  if (status === 'closed') {
    return <Button variant="secondary" loading={busy} disabled={!launchReady} title={launchReady ? undefined : launchHint} onClick={() => change('active', 'Campaign reopened')}>Reopen campaign</Button>;
  }
  return null;
}
